import React, { useCallback, useEffect, useState } from "react";
import Layout from "../../common/Layout";
import { useAuth } from "../../../contexts/AuthContext";
import dashboardService from "../../../services/dashboardService";
import useDashboardNavigation from "../common/useDashboardNavigation";
import SMActionCards from "./SMActionCards";
import SMRepLeaderboard from "./SMRepLeaderboard";
import SMSourcePerformance from "./SMSourcePerformance";
import SMOverdueHeatmap from "./SMOverdueHeatmap";
import SMWeeklyTrend from "./SMWeeklyTrend";
import SMAlerts from "./SMAlerts";
import ViewLead from "../../leads/ViewLead";
import { getUserTimezone } from "../../../utils/timezone";

function SMDashboard() {
  const { user } = useAuth();
  const nav = useDashboardNavigation();
  const userTimezone = getUserTimezone();

  const [counts, setCounts] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [sources, setSources] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [trend, setTrend] = useState(null);
  const [alerts, setAlerts] = useState(null);

  const [loading, setLoading] = useState({
    counts: true, leaderboard: true, sources: true,
    heatmap: true, trend: true, alerts: true,
  });
  const [errors, setErrors] = useState({});
  const [viewLeadId, setViewLeadId] = useState(null);

  const load = useCallback(async (key, fn, transform) => {
    setLoading((l) => ({ ...l, [key]: true }));
    try {
      const data = await fn();
      transform(data);
      setErrors((e) => ({ ...e, [key]: null }));
    } catch {
      setErrors((e) => ({ ...e, [key]: `Failed to load ${key}` }));
    } finally {
      setLoading((l) => ({ ...l, [key]: false }));
    }
  }, []);

  const refreshAll = useCallback(() => {
    load("counts", () => dashboardService.smActionCounts(), setCounts);
    load("leaderboard", () => dashboardService.smRepLeaderboard(), (d) =>
      setLeaderboard(Array.isArray(d?.data) ? d.data : []));
    load("sources", () => dashboardService.smSourcePerformance(), (d) =>
      setSources(Array.isArray(d?.data) ? d.data : []));
    load("heatmap", () => dashboardService.smOverdueHeatmap(30), setHeatmap);
    load("trend", () => dashboardService.smWeeklyTrend(4), setTrend);
    load("alerts", () => dashboardService.smAlerts(), setAlerts);
  }, [load]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const handleCardClick = (key) => {
    const map = {
      teamOverdue: () => nav.goOverdue({ salesRep: "all" }),
      teamDueToday: () => nav.goDueToday({ salesRep: "all" }),
      teamDueThisWeek: () => nav.goDueThisWeek({ salesRep: "all" }),
      unassigned: () => nav.goUnassigned(),
    };
    (map[key] || (() => {}))();
  };

  const openLead = (leadId) => setViewLeadId(leadId);
  const closeLead = () => { setViewLeadId(null); refreshAll(); };

  const firstName = (user?.fullName || "").split(" ")[0] || "there";

  return (
    <Layout>
      <div className="dashboard-v2">
        <div className="dashboard-v2-header">
          <h1>Good day, {firstName}</h1>
          <p className="dashboard-v2-subtitle">
            Team activity at a glance — overdue items, rep performance, and pipeline health.
          </p>
        </div>

        <SMActionCards
          counts={counts}
          loading={loading.counts}
          error={errors.counts}
          onCardClick={handleCardClick}
        />

        <div className="dashboard-v2-grid-2">
          <SMRepLeaderboard
            items={leaderboard}
            loading={loading.leaderboard}
            error={errors.leaderboard}
            onRowClick={(rep) => nav.goByRep(rep.user_id)}
            onCellClick={(rep, kind) => {
              const extras = { salesRep: String(rep.user_id) };
              if (kind === "overdue") nav.goOverdue(extras);
              else if (kind === "dueToday") nav.goDueToday(extras);
              else if (kind === "won") nav.goByStatus("Won", extras);
              else if (kind === "active") nav.goByRep(rep.user_id);
            }}
          />

          <SMSourcePerformance
            items={sources}
            loading={loading.sources}
            error={errors.sources}
            onSourceClick={(source) => nav.goBySource(source)}
            onCellClick={(source, kind) => {
              if (kind === "won") nav.goBySource(source, { status: "Won" });
              else nav.goBySource(source);
            }}
          />
        </div>

        <div className="dashboard-v2-grid-2">
          <SMOverdueHeatmap
            data={heatmap}
            loading={loading.heatmap}
            error={errors.heatmap}
            onCellClick={(rep) => nav.goOverdue({ salesRep: String(rep.user_id) })}
          />
          <SMWeeklyTrend
            data={trend}
            loading={loading.trend}
            error={errors.trend}
          />
        </div>

        <SMAlerts
          data={alerts}
          loading={loading.alerts}
          error={errors.alerts}
          onRepAlertClick={(rep) => nav.goOverdue({ salesRep: String(rep.user_id) })}
          onStaleClick={() => nav.goStale()}
        />

        <div className="dashboard-timezone-footer">
          <i className="fas fa-clock" />
          All times displayed in your timezone: <strong>{userTimezone}</strong>
        </div>
      </div>

      {viewLeadId && (
        <ViewLead
          leadId={viewLeadId}
          embedded={true}
          onClose={closeLead}
          onChanged={refreshAll}
        />
      )}
    </Layout>
  );
}

export default SMDashboard;